import type { NumberedStatRow } from 'ui/organisms/NumberedStatsSection/NumberedStatsSection.types';

import type { ProvenanceItem } from 'landing-pages/supply-chain-third-party-risk/sections/ProvenanceSection/ProvenanceSection';
import type { TeamUseCase } from 'landing-pages/supply-chain-third-party-risk/sections/TeamUseCasesSection/TeamUseCasesSection';

type Translate = (key: string) => string;

export const getDifferentiatorRows = (t: Translate): NumberedStatRow[] => [
  {
    n: '01',
    title: t('Primary, government-sourced'),
    body: (
      <>
        {t(
          'Read directly from the registry of record — not aggregated from resellers or inferred by a model. You see the',
        )}{' '}
        <b>{t('actual filing')}</b>
        {t(", not a black-box rating you can't interrogate.")}
      </>
    ),
  },
  {
    n: '02',
    title: t('A source on every field'),
    body: (
      <>
        {t(
          'Registry name, filing link, and retrieval timestamp travel with each attribute. When an auditor asks where a fact came from, the answer is',
        )}{' '}
        <b>{t('already attached')}</b>.
      </>
    ),
  },
  {
    n: '03',
    title: t('Open identifiers, no lock-in'),
    body: (
      <>
        {t('Keyed to the official registration numbers and LEIs you already hold —')}{' '}
        <b>{t('no proprietary ID')}</b> {t('to license, map, or migrate away from later.')}
      </>
    ),
  },
  {
    n: '04',
    title: t("Coverage where it's hardest"),
    body: (
      <>
        {t('400+ registries across 200+ countries, including the')} <b>{t('emerging markets')}</b>{' '}
        {t('where supply-chain and forced-labour risk actually concentrates — not just the easy jurisdictions.')}
      </>
    ),
  },
];

export const getSolutionItems = (t: Translate) => [
  {
    num: '01',
    title: t('Confirm legal identity & status'),
    description: t(
      'Legal name, registration number, incorporation, and live status — read from the registry of record, not a self-reported form.',
    ),
  },
  {
    num: '02',
    title: t('Resolve beneficial ownership'),
    description: t(
      'Trace ownership and control up to the individuals who ultimately own the supplier, with PSC and shareholding detail.',
    ),
  },
  {
    num: '03',
    title: t('Screen the company and its owners'),
    description: t(
      "Check the supplier and its beneficial owners against sanctions and PEP lists, so exposure through ownership doesn't slip past.",
    ),
  },
  {
    num: '04',
    title: t('Read financial health'),
    description: t(
      'Turnover, profit, assets and liabilities from filed accounts — plus modelled figures where accounts are thin — to judge supplier stability.',
    ),
  },
  {
    num: '05',
    title: t('Map the corporate group'),
    description: t(
      "See parents, subsidiaries, and shared owners across your supplier base to surface concentration you'd otherwise miss.",
    ),
  },
  {
    num: '06',
    title: t('Keep it current'),
    description: t(
      'Every verified supplier is monitored for change — status, ownership, sanctions and filings — so your view never goes stale.',
    ),
  },
];

export const getProvenanceItems = (t: Translate): ProvenanceItem[] => [
  {
    code: 'DE',
    title: 'LkSG',
    description: t(
      "Germany's Supply Chain Due Diligence Act — identify and document direct suppliers and their ownership.",
    ),
  },
  {
    code: 'EU',
    title: 'CSDDD',
    description: t('The EU Corporate Sustainability Due Diligence Directive — know your chain of activity partners.'),
  },
  {
    code: 'UK',
    title: 'Modern Slavery Act',
    description: t('Evidence the due diligence behind your supplier relationships and statements.'),
  },
  {
    code: 'US',
    title: 'UFLPA',
    description: t('Trace ownership and corporate links to assess forced-labour and entity-list exposure.'),
  },
];

export const getTeamUseCases = (t: Translate): TeamUseCase[] => [
  {
    team: t('Procurement'),
    title: t('Supplier onboarding & vetting'),
    description: t("Verify a new supplier's identity and ownership before the contract, straight from the registry."),
  },
  {
    team: t('Third-party risk'),
    title: t('TPRM & due diligence'),
    description: t('Run consistent due diligence across the whole third-party base, with provenance on every field.'),
  },
  {
    team: t('Supply chain'),
    title: t('Resilience & concentration'),
    description: t('Surface shared ownership and dependency risk hiding across nominally separate suppliers.'),
  },
  {
    team: t('Compliance & sanctions'),
    title: t('Screening through ownership'),
    description: t("Screen suppliers and their beneficial owners so sanctions exposure can't route around you."),
  },
  {
    team: t('ESG & sustainability'),
    title: t('Supply chain due diligence'),
    description: t('Trace and document supplier ownership to support your due-diligence obligations.'),
  },
  {
    team: t('Finance'),
    title: t('Supplier financial health'),
    description: t('Assess whether a critical supplier is financially stable before you concentrate spend.'),
  },
];

export const getProblemItems = (t: Translate) => [
  {
    num: '01',
    title: t('Is the supplier even real?'),
    description: t(
      'Shell entities, dormant companies, and misstated registration numbers pass unnoticed when no one checks the company against its official registry.',
    ),
  },
  {
    num: '02',
    title: t('Who actually controls it?'),
    description: t(
      'Ownership and control decide your sanctions and PEP exposure — and beneficial owners are almost never on the vendor form.',
    ),
  },
  {
    num: '03',
    title: t("Concentration you can't see"),
    description: t(
      'Several "independent" suppliers can trace back to one ultimate parent, quietly turning diversification into single-point failure.',
    ),
  },
  {
    num: '04',
    title: t('Financial fragility'),
    description: t(
      'A critical supplier in distress can stop your line. Filed accounts and modelled figures show it before the delivery does.',
    ),
  },
  {
    num: '05',
    title: t("It doesn't stay still"),
    description: t(
      'Status, ownership, and sanctions exposure shift long after onboarding. A point-in-time check is out of date the week after you run it.',
    ),
  },
];
